import { FileThumbnail } from "@/app/Media/Components/FilePreview";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createWorkOrderAssignmentExpense,
  uploadExpenseReceipts,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderExpenseMutations";
import { ExpensePaidBy, ExpensePaidTo, ExpenseType } from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { toast } from "sonner";
import { useWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignment";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { IMAGE_AND_PDF_ACCEPT } from "@/lib/FileUpload/accept";
import { useState } from "react";
import { Loader2Icon } from "lucide-react";
import { Button } from "@/components/catalyst/button";
import { useFormikContext } from "formik";

type UploadingReceipt = {
  id: string;
  imageUrl: string;
  mimeType: string;
};

type UploadedReceipt = {
  id: string;
  filePath: string;
  imageUrl: string;
  mimeType: string;
};

const expenseTypeOptions = [
  { label: "Ferry", value: ExpenseType.Ferry },
  { label: "Flight", value: ExpenseType.Flight },
  { label: "Accommodation", value: ExpenseType.Accommodation },
  { label: "Transportation", value: ExpenseType.Transportation },
  { label: "Other", value: ExpenseType.Other },
];

const validation = z.object({
  type: z.nativeEnum(ExpenseType),
  amount: z.number().min(1, "Amount is required"),
  description: z.string().optional().nullable(),
});

export function AddImoovaExpenseDialog({
  workOrderAssignmentId,
}: {
  workOrderAssignmentId: string;
}) {
  const { close } = useDialogState();
  const { mutateAsync: createExpense } = useGqlMutation(
    createWorkOrderAssignmentExpense,
  );
  const { mutateAsync: uploadReceipts } = useGqlMutation(uploadExpenseReceipts);
  const assignment = useWorkOrderAssignment(workOrderAssignmentId);
  const defaultCurrency = assignment.workOrder.currency;

  const [uploadingReceipts, setUploadingReceipts] = useState<
    UploadingReceipt[]
  >([]);
  const [uploadedReceipts, setUploadedReceipts] = useState<UploadedReceipt[]>(
    [],
  );

  const handleUpload = async (uploads: { file: File; filePath: string }[]) => {
    if (uploads.length === 0) return;

    const newUploading: UploadingReceipt[] = uploads.map((upload) => ({
      id: crypto.randomUUID(),
      imageUrl: URL.createObjectURL(upload.file),
      mimeType: upload.file.type,
    }));

    setUploadingReceipts((prev) => [...prev, ...newUploading]);

    const results = await Promise.allSettled(
      uploads.map(async (upload, index) => ({
        id: newUploading[index].id,
        filePath: upload.filePath,
        imageUrl: newUploading[index].imageUrl,
        mimeType: newUploading[index].mimeType,
      })),
    );

    const successful = results
      .filter(
        (r): r is PromiseFulfilledResult<UploadedReceipt> =>
          r.status === "fulfilled",
      )
      .map((r) => r.value);

    setUploadedReceipts((prev) => [...prev, ...successful]);
    setUploadingReceipts((prev) =>
      prev.filter((p) => !newUploading.some((n) => n.id === p.id)),
    );
  };

  const handleRemoveReceipt = (id: string) => {
    setUploadedReceipts((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <BaseDialog title="Add Imoova Expense">
      <GenericForm<z.infer<typeof validation>>
        initialValues={{
          type: ExpenseType.Ferry,
          amount: null!,
          description: "",
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          try {
            const result = await createExpense({
              input: {
                workOrderAssignment: { connect: workOrderAssignmentId },
                type: values.type,
                paid_by: ExpensePaidBy.Imoova,
                paid_to: ExpensePaidTo.ThirdParty,
                amount: values.amount,
                currency: defaultCurrency,
                description: values.description,
              },
            });

            const expenseId = result.createWorkOrderAssignmentExpense.id;

            if (uploadedReceipts.length > 0) {
              try {
                await uploadReceipts({
                  id: expenseId,
                  filePaths: uploadedReceipts.map((r) => r.filePath),
                });
              } catch {
                toast.error(
                  "Expense created but receipts failed to attach. Please add them manually.",
                );
                await close();
                return;
              }
            }

            toast.success("Expense recorded successfully");
            await close();
          } catch {
            toast.error("Failed to create expense");
          }
        }}
      >
        <SelectInput
          name="type"
          label="Expense Type"
          className="col-span-full"
          options={[{ label: "", options: expenseTypeOptions }]}
        />
        <CurrencyInput
          name="amount"
          label="Budget Amount"
          currency={defaultCurrency}
          autoFocus
        />
        <DescriptionField />
        <ReceiptsSection
          uploadingReceipts={uploadingReceipts}
          uploadedReceipts={uploadedReceipts}
          onUpload={handleUpload}
          onRemoveReceipt={handleRemoveReceipt}
        />
        <DialogSubmitButton text="Add Expense" />
      </GenericForm>
    </BaseDialog>
  );
}

function DescriptionField() {
  const { values } = useFormikContext<{ type: ExpenseType }>();

  if (values.type !== ExpenseType.Other) return null;

  return (
    <TextAreaInput
      name="description"
      label="Description"
      className="col-span-full"
    />
  );
}

function ReceiptsSection({
  uploadingReceipts,
  uploadedReceipts,
  onUpload,
  onRemoveReceipt,
}: {
  uploadingReceipts: UploadingReceipt[];
  uploadedReceipts: UploadedReceipt[];
  onUpload: (uploads: { file: File; filePath: string }[]) => Promise<void>;
  onRemoveReceipt: (id: string) => void;
}) {
  return (
    <div className="col-span-full space-y-3">
      <label className="text-sm font-medium text-zinc-950 dark:text-white">
        Receipts
      </label>

      {uploadedReceipts.map((receipt) => (
        <div
          key={receipt.id}
          className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-900"
        >
          <FileThumbnail
            src={receipt.imageUrl}
            mimeType={receipt.mimeType}
            alt="Receipt"
            className="size-12 rounded border object-cover"
          />
          <div className="flex-1 text-sm">
            <p className="font-medium text-gray-900 dark:text-white">
              Receipt uploaded
            </p>
          </div>
          <Button
            plain
            onClick={() => onRemoveReceipt(receipt.id)}
            type="button"
          >
            Remove
          </Button>
        </div>
      ))}

      {uploadingReceipts.map((receipt) => (
        <div
          key={receipt.id}
          className="flex items-center gap-3 rounded-lg border bg-zinc-50 p-3 dark:bg-zinc-900"
        >
          <FileThumbnail
            src={receipt.imageUrl}
            mimeType={receipt.mimeType}
            alt="Receipt"
            className="size-12 rounded border object-cover"
          />
          <Loader2Icon className="text-primary size-4 animate-spin" />
          <span className="text-sm text-gray-500">Uploading receipt...</span>
        </div>
      ))}

      <FileUploader
        onUpload={onUpload}
        maxFileCount={10}
        className="!h-auto"
        accept={IMAGE_AND_PDF_ACCEPT}
        fileTypeLabel="Supports JPEG, PNG, WebP, AVIF, and PDF formats"
      />
    </div>
  );
}
